import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { AlertCircle, TrendingDown, Calendar } from "lucide-react";
import moment from "moment";
import { formatarDataGuardada } from "../../../shared/util/datasGuardadas";
import "./StockDashboard.scss";

const StockDashboard = ({ stock, categoriasStock }) => {
  const [alertData, setAlertData] = useState({
    lowStock: [],
    expiringSoon: [],
    expired: [],
  });

  const [summaryData, setSummaryData] = useState({
    totalProducts: 0,
    totalItems: 0,
    lowStockPercentage: 0,
    categories: [],
  });

  const getCategoryNameById = (categoryId) => {
    if (!categoriasStock) return categoryId.substring(0, 8) + "...";

    const category = categoriasStock.find((cat) => cat.id === categoryId);
    return category ? category.nome : categoryId.substring(0, 8) + "...";
  };

  useEffect(() => {
    if (stock && stock.stock) {
      // Filtrar produtos com estoque baixo
      const lowStockItems = stock.stock.filter(
        (item) =>
          item.quantidadeTotal < item.quantidadeIdeal &&
          item.quantidadeTotal > 0,
      );

      // Produtos esgotados
      const outOfStockItems = stock.stock.filter((item) => {
        // Verificar produtos com quantidade zero
        if (item.quantidadeTotal === 0) return true;

        // Verificar se TODOS os lotes estão marcados como esgotados
        if (item.lotes.length > 0 && item.lotes.every((lote) => lote.esgotado))
          return true;

        return false;
      });

      // Identificar produtos a vencer e vencidos
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      const expiringSoonItems = [];
      const expiredItems = [];

      stock.stock.forEach((item) => {
        item.lotes.forEach((lote) => {
          if (lote.dataValidade) {
            const validadeDate = new Date(lote.dataValidade);
            if (validadeDate < today) {
              expiredItems.push({
                ...item,
                lote: lote.lote,
                validade: lote.dataValidade,
                quantidade: lote.quantidade,
                armazem: lote.armazem || "N/A",
                localizacao: lote.localizacao || "N/A",
                autoGerado: lote.autoGerado,
              });
            } else if (validadeDate <= thirtyDaysFromNow) {
              expiringSoonItems.push({
                ...item,
                lote: lote.lote,
                validade: lote.dataValidade,
                quantidade: lote.quantidade,
                armazem: lote.armazem || "N/A",
                localizacao: lote.localizacao || "N/A",
                autoGerado: lote.autoGerado,
              });
            }
          }
        });
      });

      // Preparar dados para gráficos
      setAlertData({
        lowStock: lowStockItems
          .map((item) => ({
            name:
              item.produto.nome.length > 15
                ? item.produto.nome.substring(0, 15) + "..."
                : item.produto.nome,
            atual: item.quantidadeTotal,
            ideal: item.quantidadeIdeal,
            diff: item.quantidadeIdeal - item.quantidadeTotal,
          }))
          .sort((a, b) => b.ideal - b.atual - (a.ideal - a.atual)),
        expiringSoon: expiringSoonItems,
        expired: expiredItems,
        outOfStock: outOfStockItems,
      });

      // Calcular dados de resumo
      const totalProducts = stock.stock.length;
      const totalItems = stock.stock.reduce(
        (sum, item) => sum + item.quantidadeTotal,
        0,
      );
      const lowStockPercentage =
        totalProducts > 0 ? (lowStockItems.length / totalProducts) * 100 : 0;
      const outOfStockPercentage =
        totalProducts > 0 ? (outOfStockItems.length / totalProducts) * 100 : 0;

      // Agrupar por categoria
      const categoriesMap = {};
      stock.stock.forEach((item) => {
        const catId = item.produto.categoriaStock;
        const catName = getCategoryNameById(catId);

        if (!categoriesMap[catId]) {
          categoriesMap[catId] = {
            id: catId,
            name: catName,
            count: 0,
            value: 0,
          };
        }
        categoriesMap[catId].count += 1;
        categoriesMap[catId].value += item.quantidadeTotal;
      });

      setSummaryData({
        totalProducts,
        totalItems,
        lowStockPercentage,
        outOfStockPercentage,
        categories: Object.values(categoriesMap),
      });
    }
  }, [stock]);

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884d8",
    "#82ca9d",
  ];

  return (
    <div className="stock-dashboard">
      <div className="stock-dashboard__summary">
        <div className="stock-dashboard__summary-card">
          <div className="stock-dashboard__summary-card-title">
            <h3>Total de Produtos</h3>
          </div>
          <div className="stock-dashboard__summary-card-content">
            <p className="stock-dashboard__summary-value">
              {summaryData.totalProducts}
            </p>
          </div>
        </div>

        <div className="stock-dashboard__summary-card">
          <div className="stock-dashboard__summary-card-title">
            <h3>Total de Itens</h3>
          </div>
          <div className="stock-dashboard__summary-card-content">
            <p className="stock-dashboard__summary-value">
              {summaryData.totalItems}
            </p>
          </div>
        </div>

        <div className="stock-dashboard__summary-card stock-dashboard__summary-card--alert">
          <div className="stock-dashboard__summary-card-title">
            <h3>Estoque Baixo</h3>
          </div>
          <div className="stock-dashboard__summary-card-content">
            <p className="stock-dashboard__summary-value">
              {alertData.lowStock.length}
            </p>
            <p className="stock-dashboard__summary-percentage">
              {summaryData.lowStockPercentage.toFixed(1)}% do estoque
            </p>
          </div>
        </div>

        <div className="stock-dashboard__summary-card stock-dashboard__summary-card--danger">
          <div className="stock-dashboard__summary-card-title">
            <h3>Esgotados</h3>
          </div>
          <div className="stock-dashboard__summary-card-content">
            <p className="stock-dashboard__summary-value">
              {alertData.outOfStock?.length || 0}
            </p>
            <p className="stock-dashboard__summary-percentage">
              {summaryData.outOfStockPercentage?.toFixed(1) || 0}% do estoque
            </p>
          </div>
        </div>
      </div>

      <div className="stock-dashboard__charts">
        {/* Card para produtos com estoque baixo */}
        <div className="stock-dashboard__chart-card">
          <div className="stock-dashboard__chart-card-header">
            <TrendingDown className="stock-dashboard__chart-icon stock-dashboard__chart-icon--warning" />
            <h3>Produtos com Estoque Baixo</h3>
            <span className="stock-dashboard__badge stock-dashboard__badge--warning">
              {alertData.lowStock.length}
            </span>
          </div>
          <div className="stock-dashboard__chart-content">
            {alertData.lowStock.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={alertData.lowStock.slice(0, 5)}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} />
                  <Tooltip
                    formatter={(value, name) => {
                      return [value, name === "atual" ? "Atual" : "Ideal"];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="atual" fill="#8884d8" name="Estoque Atual" />
                  <Bar dataKey="ideal" fill="#82ca9d" name="Estoque Ideal" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="stock-dashboard__empty-chart">
                <p>Todos os produtos estão com estoque adequado</p>
              </div>
            )}
          </div>
        </div>

        {/* Card para produtos a vencer */}
        <div className="stock-dashboard__chart-card">
          <div className="stock-dashboard__chart-card-header">
            <Calendar className="stock-dashboard__chart-icon stock-dashboard__chart-icon--warning" />
            <h3>Produtos a Vencer (30 dias)</h3>
            <span className="stock-dashboard__badge stock-dashboard__badge--warning">
              {alertData.expiringSoon.length}
            </span>
          </div>
          <div className="stock-dashboard__chart-content">
            {alertData.expiringSoon.length > 0 ? (
              <div className="stock-dashboard__table-wrapper">
                <table className="stock-dashboard__table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Lote</th>
                      <th>Validade</th>
                      <th>Qtd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alertData.expiringSoon.slice(0, 5).map((item, index) => (
                      <tr key={index}>
                        <td>{item.produto.nome}</td>
                        <td>{item.lote}</td>
                        <td className="stock-dashboard__date-warning">
                          {formatarDataGuardada(item.validade)}
                        </td>
                        <td>{item.quantidade}</td>
                      </tr>
                    ))}
                    {alertData.expiringSoon.length > 5 && (
                      <tr>
                        <td colSpan="4" className="stock-dashboard__table-more">
                          + {alertData.expiringSoon.length - 5} mais produtos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="stock-dashboard__empty-chart">
                <p>Nenhum produto a vencer nos próximos 30 dias</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stock-dashboard__charts">
        {/* Gráfico de distribuição por categoria */}
        <div className="stock-dashboard__chart-card">
          <div className="stock-dashboard__chart-card-header">
            <h3>Distribuição por Categoria</h3>
          </div>
          <div className="stock-dashboard__chart-content">
            {summaryData.categories.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={summaryData.categories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {summaryData.categories.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="stock-dashboard__empty-chart">
                <p>Não há dados para exibir</p>
              </div>
            )}
          </div>
        </div>

        {/* Card para produtos vencidos */}
        <div className="stock-dashboard__chart-card">
          <div className="stock-dashboard__chart-card-header">
            <AlertCircle className="stock-dashboard__chart-icon stock-dashboard__chart-icon--danger" />
            <h3>Produtos Vencidos</h3>
            <span className="stock-dashboard__badge stock-dashboard__badge--danger">
              {alertData.expired.length}
            </span>
          </div>
          <div className="stock-dashboard__chart-content">
            {alertData.expired.length > 0 ? (
              <div className="stock-dashboard__table-wrapper">
                <table className="stock-dashboard__table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Lote</th>
                      <th>Validade</th>
                      <th>Qtd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alertData.expired.slice(0, 5).map((item, index) => (
                      <tr key={index}>
                        <td>{item.produto.nome}</td>
                        <td>{item.lote}</td>
                        <td className="stock-dashboard__date-expired">
                          {formatarDataGuardada(item.validade)}
                        </td>
                        <td>{item.quantidade}</td>
                      </tr>
                    ))}
                    {alertData.expired.length > 5 && (
                      <tr>
                        <td colSpan="4" className="stock-dashboard__table-more">
                          + {alertData.expired.length - 5} mais produtos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="stock-dashboard__empty-chart">
                <p>Nenhum produto vencido no estoque</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockDashboard;
